<template>
  <div class="person-container">
    <ul class="nav nav-tabs">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'generator' }" href="#" @click.prevent="activeTab = 'generator'">Generator</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'validator' }" href="#" @click.prevent="activeTab = 'validator'">Validator</a>
      </li>
    </ul>

    <!-- Generator Tab -->
    <div v-if="activeTab === 'generator'" class="tab-content">
      <h3>Generer fiktive personopplysninger</h3>
      <div class="description-text">
        Generer fiktive telefonnumre for testing. Dataene genereres i din egen nettleser.
      </div>

      <div class="row mt-4">
        <!-- Phone Number Generator -->
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">Telefonnummer Generator</h5>
            </div>
            <div class="card-body">
              <p class="text-muted small">Generer et tilfeldig telefonnummer for valgt land.</p>

              <div class="mb-3">
                <label for="phoneCountry" class="form-label">Land</label>
                <select class="form-select" id="phoneCountry" v-model="selectedCountry">
                  <option v-for="country in countries" :key="country.code" :value="country.code">
                    {{ country.flag }} {{ country.name }} ({{ country.prefix }})
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label for="phoneFormat" class="form-label">Format</label>
                <select class="form-select" id="phoneFormat" v-model="selectedFormat">
                  <option value="international">Internasjonalt ({{ currentCountry.prefix }} ...)</option>
                  <option value="national">Nasjonalt ({{ currentCountry.nationalExample }})</option>
                </select>
              </div>

              <button @click="genererTelefonnummer" class="btn btn-primary mb-3">Generer Telefonnummer</button>

              <div v-if="generatedPhoneNumbers.length > 0" class="generated-list">
                <div v-for="phone in generatedPhoneNumbers" :key="phone.uuid" class="generated-item card mb-2">
                  <div class="card-content">
                    <button @click="fjernTelefonnummer(phone.uuid)" type="button" class="btn-close close-button" aria-label="Close"></button>
                    <p class="mb-1">
                      <span class="badge bg-primary">{{ phone.flag }}</span>
                      <span class="badge bg-secondary">{{ phone.countryName }}</span>
                    </p>
                    <p class="mb-0">
                      {{ phone.number }}
                      <button @click="leggVerdiTilClipboard(phone.number)" type="button" class="copy-button" title="Kopier til utklippstavle">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16">
                          <path fill-rule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"></path>
                          <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"></path>
                          <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"></path>
                        </svg>
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Validator Tab -->
    <div v-if="activeTab === 'validator'" class="tab-content">
      <h3>Valider personopplysninger</h3>
      <p class="mb-4">Validering av telefonnumre kommer snart.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Person',
  data() {
    return {
      activeTab: 'generator',
      selectedCountry: 'NO',
      selectedFormat: 'international',
      generatedPhoneNumbers: [],
      countries: [
        {
          code: 'NO',
          name: 'Norge',
          flag: '🇳🇴',
          prefix: '+47',
          nationalExample: '4XX XX XXX',
          generate: () => this.generateNorwegianNumber()
        },
        {
          code: 'DK',
          name: 'Danmark',
          flag: '🇩🇰',
          prefix: '+45',
          nationalExample: 'XX XX XX XX',
          generate: () => this.generateDanishNumber()
        },
        {
          code: 'SE',
          name: 'Sverige',
          flag: '🇸🇪',
          prefix: '+46',
          nationalExample: '07X-XXX XX XX',
          generate: () => this.generateSwedishNumber()
        },
        {
          code: 'DE',
          name: 'Tyskland',
          flag: '🇩🇪',
          prefix: '+49',
          nationalExample: '01XX XXXXXXX',
          generate: () => this.generateGermanNumber()
        },
        {
          code: 'FR',
          name: 'Frankrike',
          flag: '🇫🇷',
          prefix: '+33',
          nationalExample: '06 XX XX XX XX',
          generate: () => this.generateFrenchNumber()
        },
        {
          code: 'GB',
          name: 'Storbritannia',
          flag: '🇬🇧',
          prefix: '+44',
          nationalExample: '07XXX XXXXXX',
          generate: () => this.generateBritishNumber()
        },
        {
          code: 'IT',
          name: 'Italia',
          flag: '🇮🇹',
          prefix: '+39',
          nationalExample: '3XX XXX XXXX',
          generate: () => this.generateItalianNumber()
        },
        {
          code: 'FI',
          name: 'Finland',
          flag: '🇫🇮',
          prefix: '+358',
          nationalExample: '04X XXX XXXX',
          generate: () => this.generateFinnishNumber()
        },
        {
          code: 'RO',
          name: 'Romania',
          flag: '🇷🇴',
          prefix: '+40',
          nationalExample: '07XX XXX XXX',
          generate: () => this.generateRomanianNumber()
        }
      ]
    }
  },
  computed: {
    currentCountry() {
      return this.countries.find(c => c.code === this.selectedCountry) || this.countries[0]
    }
  },
  methods: {
    uuidv4() {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      )
    },
    leggVerdiTilClipboard(value) {
      if (!navigator || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert('Copy to clipboard is not supported in this browser. Please copy the text manually.')
        }
        return
      }

      navigator.clipboard.writeText(value).catch((err) => {
        if (typeof console !== 'undefined' && typeof console.error === 'function') {
          console.error('Failed to copy to clipboard:', err)
        }
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert('Failed to copy to clipboard. Please copy the text manually.')
        }
      })
    },
    randomDigit() {
      return Math.floor(Math.random() * 10)
    },
    randomDigitNonZero() {
      return Math.floor(Math.random() * 9) + 1
    },
    randomFromArray(arr) {
      return arr[Math.floor(Math.random() * arr.length)]
    },

    // Norwegian mobile: 4XX XX XXX or 9XX XX XXX
    generateNorwegianNumber() {
      const prefix = this.randomFromArray(['4', '9'])
      let number = prefix
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+47 ' + number.substring(0, 3) + ' ' + number.substring(3, 5) + ' ' + number.substring(5),
        national: number.substring(0, 3) + ' ' + number.substring(3, 5) + ' ' + number.substring(5)
      }
    },

    // Danish mobile: 8 digits, starts with 2, 3, 4, 5, or 9
    generateDanishNumber() {
      const prefix = this.randomFromArray(['2', '3', '4', '5', '9'])
      let number = prefix
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+45 ' + number.substring(0, 2) + ' ' + number.substring(2, 4) + ' ' + number.substring(4, 6) + ' ' + number.substring(6),
        national: number.substring(0, 2) + ' ' + number.substring(2, 4) + ' ' + number.substring(4, 6) + ' ' + number.substring(6)
      }
    },

    // Swedish mobile: 07X-XXX XX XX (9 digits without leading 0)
    generateSwedishNumber() {
      const prefix = '7' + this.randomFromArray(['0', '2', '3', '6', '9'])
      let number = prefix
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+46 ' + number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5, 7) + ' ' + number.substring(7),
        national: '0' + number.substring(0, 2) + '-' + number.substring(2, 5) + ' ' + number.substring(5, 7) + ' ' + number.substring(7)
      }
    },

    // German mobile: 15X, 16X, 17X followed by 7-8 digits
    generateGermanNumber() {
      const prefix = this.randomFromArray(['15', '16', '17'])
      const thirdDigit = this.randomDigit()
      let number = prefix + thirdDigit
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+49 ' + number.substring(0, 3) + ' ' + number.substring(3),
        national: '0' + number.substring(0, 3) + ' ' + number.substring(3)
      }
    },

    // French mobile: 6XX or 7XX followed by 6 digits
    generateFrenchNumber() {
      const prefix = this.randomFromArray(['6', '7'])
      let number = prefix
      for (let i = 0; i < 8; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+33 ' + number.charAt(0) + ' ' + number.substring(1, 3) + ' ' + number.substring(3, 5) + ' ' + number.substring(5, 7) + ' ' + number.substring(7),
        national: '0' + number.charAt(0) + ' ' + number.substring(1, 3) + ' ' + number.substring(3, 5) + ' ' + number.substring(5, 7) + ' ' + number.substring(7)
      }
    },

    // British mobile: 7XXX followed by 6 digits
    generateBritishNumber() {
      const prefix = '7' + this.randomFromArray(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'])
      let number = prefix
      for (let i = 0; i < 8; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+44 ' + number.substring(0, 4) + ' ' + number.substring(4),
        national: '0' + number.substring(0, 4) + ' ' + number.substring(4)
      }
    },

    // Italian mobile: 3XX followed by 6-7 digits
    generateItalianNumber() {
      const prefix = '3' + this.randomFromArray(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'])
      let number = prefix
      for (let i = 0; i < 8; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+39 ' + number.substring(0, 3) + ' ' + number.substring(3, 6) + ' ' + number.substring(6),
        national: number.substring(0, 3) + ' ' + number.substring(3, 6) + ' ' + number.substring(6)
      }
    },

    // Finnish mobile: 04X or 05X followed by 7 digits
    generateFinnishNumber() {
      const prefix = this.randomFromArray(['40', '41', '42', '43', '44', '45', '46', '50'])
      let number = prefix
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+358 ' + number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5),
        national: '0' + number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5)
      }
    },

    // Romanian mobile: 07XX XXX XXX (9 digits without leading 0)
    generateRomanianNumber() {
      const prefix = '7' + this.randomFromArray(['2', '3', '4', '5', '6', '7', '8', '9'])
      let number = prefix
      for (let i = 0; i < 7; i++) {
        number += this.randomDigit()
      }
      return {
        raw: number,
        international: '+40 ' + number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5),
        national: '0' + number.substring(0, 2) + ' ' + number.substring(2, 5) + ' ' + number.substring(5)
      }
    },

    genererTelefonnummer() {
      const country = this.currentCountry
      const phoneData = country.generate()

      const formattedNumber = this.selectedFormat === 'international'
        ? phoneData.international
        : phoneData.national

      this.generatedPhoneNumbers.push({
        uuid: this.uuidv4(),
        number: formattedNumber,
        raw: phoneData.raw,
        countryCode: country.code,
        countryName: country.name,
        flag: country.flag,
        format: this.selectedFormat
      })
    },

    fjernTelefonnummer(uuid) {
      this.generatedPhoneNumbers = this.generatedPhoneNumbers.filter(p => p.uuid !== uuid)
    }
  }
}
</script>

<style scoped>
.person-container {
  padding: 10px;
}

.nav-tabs {
  margin-bottom: 20px;
}

.tab-content {
  padding: 10px;
}

.description-text {
  width: 80%;
  margin: 20px 0;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.badge {
  margin-left: 0.5rem;
}

.form-label {
  font-weight: 500;
}

.generated-list {
  margin-top: 15px;
}

.generated-item {
  position: relative;
}

.card-content {
  position: relative;
  padding: 10px;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
}

.copy-button {
  margin-left: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.copy-button:hover {
  opacity: 0.7;
}
</style>
